import { ListOrderStatus } from '../../list/types/ListOrderStatus';
import { IMoney } from '../../types/IMoney';
import { IFileResponse } from '@lib/core';
import { ICompanyAddressDto } from '@lib/address';
import { InvoicePossibleActionType } from './InvoicePossibleActionType';
import { InvoiceStatus } from '@lib/invoice-public';
import { CreateInvoiceCharge } from '../../create-standalone/types/StandaloneInvoiceDraft';
import { CustomerDepartmentDto } from '@lib/department/ui';
import { ILinkedDocumentDto } from './LinkedDocument';
import { InvoiceDocumentType } from '../../types/InvoiceDocumentType';

export interface IQuantitySimpleDto {
    amount: number;
    unit: string;
}

export interface IInvoiceItemOrderRelationViewDto {
    orderId: string;
    orderErpNumber: string | null;
    orderItemId: number;
    orderItemPosition: number;
    supplierName: string;
    orderDate: string;
    status: ListOrderStatus;
}

export interface IInvoiceItemReceiptRelationViewDto {
    receiptId: number;
    receiptPosition: number;
    receiptNumber: string;
    isOpen: boolean;
    inApproval: boolean;
    deliveryDate: string;
    creationDate: string;
}

/**
 * Ordered, delivered and invoiced quantity of the order item an invoice item is linked to
 * (EPROC-12025). Purely informational. Only present on items whose order item could be read.
 */
export interface IInvoiceItemOrderQuantitiesViewDto {
    orderedQuantity: number;
    deliveredQuantity: number;
    /** Invoiced across all live invoices - every invoice except drafts, cancelled and rejected ones. */
    invoicedQuantity: number;
    unit: string | null;
}

export interface IInvoiceItemViewDto {
    id: string;
    position: number;
    name: string;
    description: string;
    supplierArticleNumber: string;
    quantity: IQuantitySimpleDto;
    netPrice: IMoney;
    unitPrice: IMoney;
    tax: IInvoiceViewTax;
    priceQuantity: number;
    /** Optional discount percentage (0-100) applied to this item */
    discountPercentage?: number;
    materialGroupCode?: string | null;
    accounting: IStandaloneInvoiceItemAccountingDto;
    orderRelation?: IInvoiceItemOrderRelationViewDto;
    receiptRelation?: IInvoiceItemReceiptRelationViewDto;
    orderItemQuantities?: IInvoiceItemOrderQuantitiesViewDto;
}

export interface IInvoiceItemCustomAccountingView {
    id: string;
    type: number | null;
    name: string;
    code: string;
}

export interface IStandaloneInvoiceItemAccountingDto {
    costCenter?: string;
    project?: string;
    generalLedgerAccount?: string;
    customAccountings: IInvoiceItemCustomAccountingView[];
}

export interface IInvoiceViewTax {
    percent: number;
    amount: number;
}

export interface IInvoiceViewPrice {
    totalNetPrice: number;
    totalGrossPrice: number;
    currency: string;
    taxes: IInvoiceViewTax[];
}

export interface IInvoiceSupplierViewDto {
    id: string;
    supplierNumber?: string | null;
    companyAddress: ICompanyAddressDto;
}

export interface IStandaloneInvoiceDetailDto {
    id: string;
    invoiceNumber: string;
    companyCodeId?: string;
    status: InvoiceStatus;
    documentType?: InvoiceDocumentType;
    createDate: string;
    invoiceDate: string;
    paymentDate: string;
    paymentUntil: string;
    remark: string;
    messageToBuyer: string;
    supplier: IInvoiceSupplierViewDto;
    price: IInvoiceViewPrice | null;
    comment: string;
    items: IInvoiceItemViewDto[];
    invoiceAddress: IInvoiceAddressDto;
    files: IFileResponse[];
    history: IInvoiceHistory;
    department: CustomerDepartmentDto;
    charges: CreateInvoiceCharge[];
    possibleActions: InvoicePossibleActionType[];
    linkedDocuments: ILinkedDocumentDto[];
}

export interface IInvoiceAddressDto {
    vatNo: string;
    addressCode: string;
    companyAddress: ICompanyAddressDto;
}

export interface IInvoiceHistoryAuthor {
    id: string;
    name: string;
}

export interface IInvoiceHistoryComment {
    id: string;
    author: IInvoiceHistoryAuthor;
    superUser?: IInvoiceHistoryAuthor;
    comment: string;
    isInternal: boolean;
    timestamp: string;
    files: IFileResponse[];
}

export interface IInvoiceHistory {
    comments: IInvoiceHistoryComment[];
}

export class InvoiceHistory implements IInvoiceHistory {
    constructor(public readonly comments: IInvoiceHistoryComment[]) {}
}
